import React, { useState } from "react";
import Title from "../../Other/Title";
import { motion, AnimatePresence } from "framer-motion";
import ImageBG from "../../../assets/icons/image_bg.svg";
import UlakLogo from "../../../assets/images/companies/ulak.jpg"
import HirehootLogo from "../../../assets/images/companies/hirehoot.jpg"
import WesteropsLogo from "../../../assets/images/companies/westerops.jpg"
import SvaypLogo from "../../../assets/images/companies/svayp.jpg"
import ImzaLogo from "../../../assets/images/companies/imza.jpg"
import { useLanguage } from "../../../i18n/LanguageContext";

const WorkExperiancesData = [
    {
        date: { en: "Dec 2025 - Present", tr: "Ara 2025 - Halen" },
        image: UlakLogo,
        title: { en: "Computer Engineer", tr: "Bilgisayar Mühendisi" },
        company: "ulak cx",
        companyLink: "https://ulakcx.com",
        country: `🇹🇷`,
    },
    {
        date: { en: "Feb 2025 - Present", tr: "Şub 2025 - Halen" },
        image: HirehootLogo,
        title: { en: "Frontend Developer", tr: "Frontend Geliştirici" },
        company: "hirehoot",
        companyLink: "https://hirehoot.net/",
        country: `🇬🇧`,
    },
    {
        date: { en: "Jun 2024 - Jan 2025", tr: "Haz 2024 - Oca 2025" },
        image: WesteropsLogo,
        title: { en: "Frontend Developer", tr: "Frontend Geliştirici" },
        company: "westerops",
        companyLink: "https://westerops.com",
        country: `🇩🇪 | 🇹🇷`,
    },
    {
        date: { en: "Dec 2023 - Nov 2024", tr: "Ara 2023 - Kas 2024" },
        image: ImzaLogo,
        title: { en: "Frontend Developer", tr: "Frontend Geliştirici" },
        company: "imza.group",
        companyLink: "https://eimzagroup.com",
        country: "🇹🇷",
    },
    {
        date: { en: "Mar 2023 - Apr 2025", tr: "Mar 2023 - Nis 2025" },
        image: SvaypLogo,
        title: { en: "Frontend Web Developer", tr: "Frontend Web Geliştirici" },
        company: "svayp",
        companyLink: "https://svayp.com",
        country: "🇸🇬",
    }
]

const imageContainerVariants = {
  initial: { scale: 1 },
  hover: {
    scale: 1.1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 10,
      delayChildren: 0.1,
      staggerChildren: 0.1
    }
  }
};

const imageVariants = {
  initial: { scale: 1, rotate: 0 },
  hover: {
    scale: 1.2,
    rotate: 5,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 10
    }
  }
};

const textVariants = {
  initial: { y: 0, opacity: 1 },
  hover: {
    y: -3,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 10
    }
  }
};

const linkVariants = {
  initial: { scale: 1 },
  hover: {
    scale: 1.1,
    color: "#a5b4fc",
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 10
    }
  }
};

const particleVariants = {
  initial: {
    opacity: 0,
    scale: 0,
    y: 0
  },
  animate: {
    opacity: [0, 1, 0],
    scale: [0, 1, 0.5],
    y: -20,
    transition: {
      duration: 2,
      repeat: Infinity,
      repeatType: "loop",
      ease: "easeInOut"
    }
  }
};

const ExperienceCard = ({ data, cardKey, hoveredCard, setHoveredCard, lang }) => (
  <motion.div
    whileHover={{
      y: -15,
      scale: 1.05,
      rotateY: 2,
      boxShadow: "0 25px 30px -5px rgba(0, 0, 0, 0.3), 0 15px 15px -5px rgba(0, 0, 0, 0.2)",
      transition: { type: "spring", stiffness: 300, damping: 15 }
    }}
    className="group shrink-0 w-64 flex items-center justify-center flex-col bg-gradient-to-b from-[#2a2937] to-[#242331] hover:from-[#2d2c3a] hover:to-[#242331] shadow-lg transition-all px-7 py-4 rounded-lg border-[rgba(67,65,85,0.4)] border-[0.5px] relative overflow-hidden perspective-500"
    onMouseEnter={() => setHoveredCard(cardKey)}
    onMouseLeave={() => setHoveredCard(null)}
  >
    {/* Particle effects */}
    <AnimatePresence>
      {hoveredCard === cardKey && (
        <>
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={`particle-${i}`}
              variants={particleVariants}
              initial="initial"
              animate="animate"
              exit={{ opacity: 0, scale: 0 }}
              className="absolute rounded-full bg-indigo-500/30"
              style={{
                width: Math.random() * 6 + 2,
                height: Math.random() * 6 + 2,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`
              }}
            />
          ))}
        </>
      )}
    </AnimatePresence>

    {/* Background glow effect */}
    <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/0 via-indigo-500/0 to-purple-500/0 group-hover:from-indigo-500/5 group-hover:via-indigo-500/5 group-hover:to-purple-500/5 transition-all duration-700"></div>

    {/* Top border gradient */}
    <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-indigo-500/0 via-indigo-500/70 to-purple-500/0"></div>

    {/* Bottom border gradient on hover */}
    <div className="absolute bottom-0 left-0 right-0 h-[2px] scale-x-0 group-hover:scale-x-100 bg-gradient-to-r from-indigo-500/0 via-indigo-500/50 to-purple-500/0 transition-transform duration-500"></div>

    {/* Left and right border gradients on hover */}
    <div className="absolute left-0 top-0 bottom-0 w-[2px] scale-y-0 group-hover:scale-y-100 bg-gradient-to-b from-indigo-500/0 via-indigo-500/30 to-purple-500/0 transition-transform duration-500 delay-100"></div>
    <div className="absolute right-0 top-0 bottom-0 w-[2px] scale-y-0 group-hover:scale-y-100 bg-gradient-to-b from-indigo-500/0 via-indigo-500/30 to-purple-500/0 transition-transform duration-500 delay-100"></div>

    <motion.div
      variants={textVariants}
      initial="initial"
      whileHover="hover"
      className="text-[rgb(147,147,147)] text-sm mt-1 mb-4 font-medium"
    >
      {data.date[lang]}
    </motion.div>

    <motion.div
      className="relative mt-2"
      variants={imageContainerVariants}
      initial="initial"
      whileHover="hover"
    >
      {/* Glow behind image */}
      <motion.div
        className="absolute inset-0 bg-indigo-500/10 rounded-full scale-0 group-hover:scale-100 transition-transform duration-500"
        style={{ filter: "blur(8px)" }}
      ></motion.div>

      <motion.img
        variants={imageVariants}
        className="absolute"
        src={ImageBG}
        alt="bg"
      />

      <motion.img
        className="rounded-full z-10 relative border-2 border-transparent group-hover:border-indigo-500 transition-all duration-300"
        width={53}
        src={data.image}
        alt={data.company}
        whileHover={{ scale: 1.05 }}
        transition={{ type: "spring", stiffness: 300, damping: 10 }}
      />
    </motion.div>

    <motion.div
      variants={textVariants}
      initial="initial"
      whileHover="hover"
      className="text-white font-semibold text-sm mt-5 text-center"
    >
      {data.title[lang]}
    </motion.div>

    <motion.a
      href={data.companyLink}
      target="_blank"
      rel="noopener noreferrer"
      className="text-indigo-400 font-medium mt-1 transition-colors relative"
      variants={linkVariants}
      initial="initial"
      whileHover="hover"
    >
      @{data.company}
      <span className="absolute -bottom-1 left-0 w-0 h-[1px] bg-indigo-400 group-hover:w-full transition-all duration-300"></span>
    </motion.a>

    <motion.div
      variants={textVariants}
      initial="initial"
      whileHover="hover"
      className="mb-2 mt-2 text-lg"
    >
      {data.country}
    </motion.div>
  </motion.div>
);

const WorkExperiances = () => {
  const { t, lang } = useLanguage();
  const calculateWorkExperianceDate = new Date().getFullYear() - 2021;
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <div className="font-inter mt-52" id="experiences">
      <Title title={`${calculateWorkExperianceDate}+ ${t.experience.years}`} />

      <div className="text-center -mt-64">
        <motion.div
          initial={{ opacity: 0 }}
          viewport={{ margin: "-120px" }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          <motion.h3
            initial={{ y: -20, opacity: 0 }}
            viewport={{ margin: "-120px" }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{
              type: "spring",
              stiffness: 100,
              damping: 10,
              delay: 0.1
            }}
            className="font-bold text-2xl mb-3 text-white"
          >
            <span className="bg-gradient-to-r from-indigo-400 to-purple-500 text-transparent bg-clip-text">
              {t.experience.title}
            </span>
          </motion.h3>
          <motion.div
            className="h-[2px] w-16 bg-gradient-to-r from-indigo-400 to-purple-500 mx-auto mt-2 mb-4"
            initial={{ width: 0 }}
            whileInView={{ width: 64 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          ></motion.div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          viewport={{ margin: "-120px" }}
          whileInView={{ opacity: 0.8, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-base max-w-xl mx-auto text-gray-300 font-light"
        >
          {t.experience.subtitle}
        </motion.p>
      </div>

      <motion.div
        className="w-full mt-14 overflow-hidden group/marquee"
        style={{
          maskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
          WebkitMaskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)"
        }}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ margin: "-50px" }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex w-max gap-5 py-8 animate-marquee group-hover/marquee:[animation-play-state:paused]">
          {[...WorkExperiancesData, ...WorkExperiancesData].map((data, index) => (
            <ExperienceCard
              key={index}
              cardKey={index}
              data={data}
              hoveredCard={hoveredCard}
              setHoveredCard={setHoveredCard}
              lang={lang}
            />
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default WorkExperiances;
